import { Tab } from "@headlessui/react";
import React from "react";
import { Link } from "react-router-dom";
import { DynamicTabs } from "./DynamicTabs";
import { TabNavigationProps } from "./types";

const TabNavigation = ({
  currentTabIndex,
  handleTabChange,
  tabs,
  actionButtons,
  dynamicTabs,
  onDynamicTabsReorder,
}: TabNavigationProps) => {
  const rightAlignedTabs = React.useMemo(
    () => tabs.filter((tab) => tab.align === "right" || tab.id === 4),
    [tabs],
  );
  const leftAlignedTabs = React.useMemo(
    () => tabs.filter((tab) => tab.align !== "right" && tab.id !== 4),
    [tabs],
  );

  return (
    <Tab.Group selectedIndex={currentTabIndex}>
      <Tab.List className="sticky top-0 z-10 my-1 flex items-center justify-between border-b border-gray-200 bg-white/50 backdrop-blur-sm">
        <div className="flex space-x-1">
          {leftAlignedTabs.map((tab) => (
            <Tab
              key={tab.id}
              as={Link}
              to={tab.path || "#"}
              onClick={(e) => {
                if (!tab.path) {
                  handleTabChange?.(tab.id);
                  e.preventDefault();
                }
                tab.onClick?.(e);
              }}
              className={`relative flex h-10 items-center whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium outline-none transition-colors duration-300 ease-in-out
                ${
                  currentTabIndex === tab.id
                    ? "border-blue-600 text-blue-600 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:translate-y-[2px] after:bg-blue-600/20 after:blur-sm"
                    : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                } ${tab.className || ""}`}
              aria-label={`${tab.name} tab`}
            >
              <span className="flex items-center">
                <span className="relative mr-2 h-4 w-4">
                  <span
                    className={`absolute inset-0 text-blue-400 transition-opacity duration-300 ${
                      currentTabIndex === tab.id ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {tab.solidIcon}
                  </span>
                  <span
                    className={`absolute inset-0 text-gray-400 transition-opacity duration-300 ${
                      currentTabIndex === tab.id ? "opacity-0" : "opacity-100"
                    }`}
                  >
                    {tab.outlineIcon}
                  </span>
                </span>
                {tab.name}
              </span>
            </Tab>
          ))}
          {!!dynamicTabs?.length && dynamicTabs.length > 0 && (
            <div className="!mx-1 min-h-full min-w-[1px] bg-gray-200" />
          )}
        </div>
        <DynamicTabs
          dynamicTabs={dynamicTabs}
          onReorder={onDynamicTabsReorder}
          handleTabChange={handleTabChange}
          currentIndex={currentTabIndex}
        />
        <div className="flex items-center">
          {rightAlignedTabs.map((tab) => (
            <Tab
              key={tab.id}
              as={Link}
              to={tab.path || "#"}
              onClick={(e) => {
                if (!tab.path) {
                  handleTabChange?.(tab.id);
                  e.preventDefault();
                }
                tab.onClick?.(e);
              }}
              className={({ selected }) =>
                `relative flex h-10 items-center border-b-2 px-4 py-2.5 text-sm font-medium outline-none transition-colors duration-300 ease-in-out
                ${
                  selected
                    ? "border-blue-600 text-blue-600 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:translate-y-[2px] after:bg-blue-600/20 after:blur-sm"
                    : "border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                } ${tab.className || ""}`
              }
              aria-label={`${tab.name} tab`}
            >
              <span className="flex items-center">
                <span className="relative mr-2 h-4 w-4">
                  <span
                    className={`absolute inset-0 text-blue-300 transition-opacity duration-300 ${
                      currentTabIndex === tab.id ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {tab.solidIcon}
                  </span>
                  <span
                    className={`absolute inset-0 text-gray-300 transition-opacity duration-300 ${
                      currentTabIndex === tab.id ? "opacity-0" : "opacity-100"
                    }`}
                  >
                    {tab.outlineIcon}
                  </span>
                </span>
                {tab.name}
              </span>
            </Tab>
          ))}
          {actionButtons && (
            <div className="ml-2 flex items-center space-x-2">
              {actionButtons}
            </div>
          )}
        </div>
      </Tab.List>
      <Tab.Panels className="flex-grow overflow-auto">
        {[...tabs, ...(dynamicTabs || [])].map((tab) => (
          <Tab.Panel
            key={tab.id}
            className="h-full rounded-xl bg-white focus:outline-none"
            tabIndex={-1}
          >
            {tab.content}
          </Tab.Panel>
        ))}
      </Tab.Panels>
    </Tab.Group>
  );
};

export default TabNavigation;
